(ns livefeed.topic-container
  (:require
   ["hammerjs" :as hammer]
   [clojure.string :as string]
   [livefeed.analytics :as analytics]
   [livefeed.feed :as feed]
   [livefeed.feed-reactions :as feed-reactions]
   [livefeed.i18n :refer [translate]]
   [livefeed.icons :as icons]
   [livefeed.message-status :as status :refer [ready-to-send?]]
   [livefeed.properties :refer [get-property]]
   [livefeed.quote :refer [message-quote]]
   [livefeed.state :as state :refer [is-at-bottom logged-in user]]
   [livefeed.tunnus :as tunnus]
   [livefeed.util :refer [timestamp->display-time timestamp->full-date]]
   [livefeed.websocket :refer [like-message unlike-message]]
   [reagent.core :as reagent :refer [after-render atom with-let]]
   [reagent.dom :as rdom]))

(defonce last-not-seen-id (reagent/atom nil))
(defonce previous-scroll (atom {:scroll-height 0
                                :scroll-top    0}))
(def images-api-url (delay (get-property :images-api-url)))

(def avatar-height 40)
(def avatar-crop "fill")
(def avatar-aspect-ratio "1:1")
(def avatar-focus "face")

(defn suppressing-ios-momentum-scrolling [element f]
  (set! (.. element -style -overflowY) "hidden")
  (f)
  (set! (.. element -style -overflowY) "scroll"))

(defn build-avatar-url [id]
  (when id
    (let [transformations (str "h_" avatar-height
                               ",crop_" avatar-crop
                               ",ar_" avatar-aspect-ratio
                               ",focus_" avatar-focus
                               ",f_jpg")]
      (str @images-api-url "/" transformations "/" id))))

(defn scroll-element-to [element top behavior]
  (after-render (fn [_]
                  (let [top (if (neg? top) 0 top)]
                    (.scrollTo element #js {:top      top
                                            :behavior (name behavior)})))))

(defn scroll-element-to-bottom
  [element behavior]
  (after-render (fn [_]
                  (let [top (.-scrollHeight element)]
                    (suppressing-ios-momentum-scrolling
                     element
                     (fn []
                       (scroll-element-to element top behavior)))
                    (reset! is-at-bottom true)
                    (reset! last-not-seen-id nil)))))

(defn component-did-mount [this]
  (let [element (rdom/dom-node this)]
    (scroll-element-to-bottom element :auto)
    (js/setTimeout #(scroll-element-to-bottom element :auto) 300)
    (reset! is-at-bottom true)))

(defn component-did-update [this old-argv _ old-height]
  (let [element (rdom/dom-node this)
        new-argv (reagent/argv this)
        [_ old-topic-items old-best?] old-argv
        [_ new-topic-items new-best?] new-argv
        initial-update? (empty? old-topic-items)]
    (cond
      (= old-argv new-argv)
      nil

      (not= old-best? new-best?)
      (scroll-element-to-bottom element :auto)

      @is-at-bottom
      (scroll-element-to-bottom element (if initial-update? :auto :smooth))

      ;; new comments added
      (and (not= (:comment-id (last old-topic-items)) (:comment-id (last new-topic-items)))
           (nil? @last-not-seen-id))
      (reset! last-not-seen-id (:comment-id (last new-topic-items)))

      ;; previous comments added
      (not= (:comment-id (first old-topic-items)) (:comment-id (first new-topic-items)))
      (after-render
       (fn []
         (let [new-scroll-top (- (.-scrollHeight element) old-height)]
           ;; Temporarily disable container scrolling to prevent "momentum" on iOS Safari
           ;; https://github.com/bvaughn/react-window/issues/122#issuecomment-455251781
           (suppressing-ios-momentum-scrolling
            element
            (fn []
              (set! (.-scrollTop element) new-scroll-top)))))))))

(defn get-snapshot-before-update [this]
  (.-scrollHeight (rdom/dom-node this)))

(defn on-scroll-impl [best?]
  (let [container (.querySelector js/document ".yle-livefeed__messages")
        {:keys [scroll-height scroll-top]} @previous-scroll
        new-scroll-height (.-scrollHeight container)
        new-scroll-top (.-scrollTop container)
        client-height (.-clientHeight container)
        was-at-bottom @is-at-bottom
        is-at-bottom-now (cond
                           ;; definitely at bottom
                           (< (- new-scroll-height new-scroll-top client-height) 20) true

                           ;; don't change status if scrolling down
                           (= scroll-height new-scroll-height) (and was-at-bottom
                                                                    (<= scroll-top new-scroll-top))

                           ;; don't change status if messages were added or removed
                           :else was-at-bottom)]
    (reset! previous-scroll {:scroll-height new-scroll-height
                             :scroll-top    new-scroll-top})
    (when (and (< new-scroll-top 5)
               (not @feed/at-feed-start?))
      (feed/load-previous-messages best?))
    (when (not= was-at-bottom is-at-bottom-now)
      (reset! is-at-bottom is-at-bottom-now)
      (reset! last-not-seen-id nil))))

(defn on-scroll [best?]
  (after-render (fn []
                  (on-scroll-impl best?))))

(defn on-unread-click [_]
  (let [element (.querySelector js/document ".yle-livefeed__messages")]
    (scroll-element-to-bottom element :smooth)))

(defn toggle-like [message-id]
  (feed/toggle-user-has-liked-message message-id)
  (if (feed/is-user-liked-message message-id)
    (do
      (analytics/track-event "comment" "liked")
      (like-message message-id))
    (do
      (analytics/track-event "comment" "unliked")
      (unlike-message message-id))))

(defn handle-like-click [message-id evt]
  (.stopPropagation evt)
  (if @state/logged-in
    (when (not= @status/status :topic-locked)
      (toggle-like message-id))
    (tunnus/login!)))

(def url-regex #"\bhttps?://[\w.~-]+\.[A-z]{2,}(?:\S*[^\s.])?")

(defn add-links [text]
  (let [links (for [link (re-seq url-regex text)]
                [:a {:href   link
                     :target :_blank
                     :rel    :noopener}
                 link])
        text-parts (string/split text url-regex)
        result (cond
                 (empty? text-parts) links
                 (= (count text-parts) (count links)) (interleave text-parts links)
                 :else (cons (first text-parts) (interleave links (rest text-parts))))]
    (into [:<>] result)))

(defn message-component [{:keys [style message replyable? focus-input]}]
  (with-let [report (fn [] (reset! feed/reported-message message))
             reply (fn []
                     (reset! feed/quoted-message message)
                     (focus-input))]
    (let [{:keys [id comment-id chat-user content acception-time role own-message? amount-of-likes like-disabled? quote]} message
          writer? (= "writer" role)
          moderator? (= "moderator" role)
          trusted? (or writer? moderator?)
          avatar (when trusted?
                   (build-avatar-url (:avatar chat-user)))]
      [:div.yle-livefeed__messages__message
       {:class   [(cond
                    trusted? :yle-livefeed__messages__message--moderator
                    own-message? :yle-livefeed__messages__message--self)
                  (when quote :yle-livefeed__messages__message--has-quote)]
        :style   style
        :data-id id}
       [:div.yle-livefeed__messages__message__header
        [:span.yle-livefeed__messages__message__header__user
         (when avatar [:img.yle-livefeed__messages__message__header__avatar {:src avatar}])
         (:nick chat-user)
         (when (feed/starred? message)
           icons/star)]
        [:span.yle-livefeed__messages__message__header__time
         {:title (timestamp->full-date acception-time)}
         (timestamp->display-time acception-time false)]]
       (when quote
         [message-quote quote])
       [:div.yle-livefeed__messages__message__content (if (and trusted? content (string? content))
                                                        (add-links content)
                                                        content)
        [:div.yle-livefeed__messages__message__content__buttons
         [:button.yle-livefeed__messages__message__report-button
          {:title (translate :report :long)
           :aria-label (translate :report :long)
           :on-click report}
          icons/flag]
         (when replyable?
           [:button.yle-livefeed__messages__message__reply-button
            {:on-click   reply
             :title      (translate :reply-to-message)
             :aria-label (translate :reply-to-message)}
            icons/reply])
         [:div.yle-livefeed__messages__message__likes
          (when like-disabled?
            [icons/thumb])
          (let [likes (when (and amount-of-likes (pos? amount-of-likes))
                        amount-of-likes)]
            (when likes
              [:div.yle-livefeed__messages__message__likes__value
               {:aria-label (str likes " " (translate :likes))}
               (or likes "")]))
          (when-not like-disabled?
            [:button.yle-livefeed__messages__message__likes__button
             {:title    (if (feed/is-user-liked-message comment-id)
                          (translate :un-like)
                          (translate :like))
              :on-click #(handle-like-click comment-id %1)
              :disabled like-disabled?}
             (icons/thumb (feed/is-user-liked-message comment-id))])]]
        [:div.yle-livefeed__messages__message__content__clear]]])))

(defn calculate-dx [deltaX replyable?]
  (let [newDeltaX (cond
                    (< deltaX -60) (+ -40 (/ deltaX 3))
                    (> deltaX 60) (+ 40 (/ deltaX 3))
                    :else deltaX)]
    (if replyable? newDeltaX (min 0 newDeltaX))))

(defn slidable-message []
  (let [hammer-manager (atom nil)
        dx (atom 0)]
    (reagent/create-class
     {:component-did-mount    (fn [this]
                                (let [hm (hammer/Manager. (rdom/dom-node this) #js {:cssProps #js {:userSelect nil}})]
                                  (reset! hammer-manager hm)
                                  (doto hm
                                    (.add (hammer/Pan. #js {:direction hammer/DIRECTION_HORIZONTAL
                                                            :threshold 20}))
                                    (.on "pan" (fn [e]
                                                 (-> e .-srcEvent .preventDefault)
                                                 (when (= (.-eventType e) js/Hammer.INPUT_MOVE)
                                                   (let [props (reagent/props this)
                                                         replyable? (:replyable? props)]
                                                     (reset! dx (calculate-dx (.-deltaX e) replyable?))))))
                                    (.on "panend" (fn [e]
                                                    (let [props (reagent/props this)
                                                          replyable? (:replyable? props)
                                                          message (:message props)
                                                          focus-input (:focus-input props)]
                                                      (when (and (>= (.-deltaX e) 60) replyable?)
                                                        (reset! feed/quoted-message message)
                                                        (focus-input))
                                                      (when (<= (.-deltaX e) -60)
                                                        (reset! feed/reported-message message))
                                                      (reset! dx 0))))
                                    (.on "pancancel" (fn [e]
                                                       (reset! dx 0))))))
      :component-will-unmount (fn []
                                (when-let [^js hm @hammer-manager]
                                  (.destroy hm)))
      :reagent-render         (fn [{:keys [message focus-input replyable?]}]
                                [:div.yle-livefeed__messages__reply-container
                                 {:class (case @dx
                                           0 :yle-livefeed__messages__reply-container--initial
                                           60 :yle-livefeed__messages__reply-container--quoting
                                           :yle-livefeed__messages__reply-container--panning)}
                                 [:div.yle-livefeed__messages__reply-container__reply-indicator
                                  {:style {:opacity (if (<= 60 @dx)
                                                      1
                                                      (/ @dx 120))}}
                                  icons/reply
                                  [:span.yle-livefeed__messages__reply-container__reply-indicator__text
                                   (translate :reply-to-message-short)]]
                                 [:div.yle-livefeed__messages__reply-container__report-indicator
                                  {:style {:opacity (if (>= -60 @dx)
                                                      1
                                                      (/ @dx -120))}}
                                  icons/flag
                                  [:span.yle-livefeed__messages__reply-container__report-indicator__text
                                   (translate :report :short)]]
                                 [message-component {:style       {:left (str @dx "px")}
                                                     :message     message
                                                     :replyable?  replyable?
                                                     :focus-input focus-input}]])})))

(defn auto-scrolling-container []
  (reagent/create-class
   {:reagent-render             (fn [topic-items best? focus-input]
                                  [:div.yle-livefeed__messages {:on-scroll #(on-scroll best?)}
                                   (when-not (seq topic-items)
                                     [:div.yle-livefeed__messages__placeholder
                                      (translate :chat-begins-soon)])
                                   (doall (for [{:keys [id comment-id chat-user content acception-time role likes quote labels]} topic-items]
                                            (let [id (or id comment-id)
                                                  own-message? (= (:id chat-user) (:id @user))]
                                              ^{:key id}
                                              [slidable-message {:message     {:id              id
                                                                               :comment-id      comment-id
                                                                               :chat-user       chat-user
                                                                               :content         content
                                                                               :acception-time  acception-time
                                                                               :role            role
                                                                               :own-message?    own-message?
                                                                               :amount-of-likes likes
                                                                               :like-disabled?  false
                                                                               :quote           quote
                                                                               :labels          labels}
                                                                 :replyable? (and @logged-in (ready-to-send?))
                                                                 :focus-input focus-input}])))])
    :component-did-update       component-did-update
    :component-did-mount        component-did-mount
    :get-snapshot-before-update get-snapshot-before-update}))

(defn unread-indicator []
  (fn []
    (let [has-unseen-messages (not (nil? @last-not-seen-id))]
      [:button.yle-livefeed__messages__more__indicator
       {:class    (when has-unseen-messages :yle-livefeed__messages__more__indicator__active)
        :aria-hidden (when-not has-unseen-messages "true")
        :on-click on-unread-click}
       (translate :unread-messages)])))

(defn loading-indicator [active?]
  [:div.yle-livefeed__messages__loading-indicator
   {:class (when active? :yle-livefeed__messages__loading-indicator--active)}
   [:div.yle-livefeed__messages__loading-indicator__spinner
    [icons/spinner {:class :messages-loading-indicator}]]])

(defn component [topic-items best? loading? focus-input]
  [:h2.yle-livefeed__messages__header (translate :header)]
  (if (nil? topic-items)
    [:div.yle-livefeed__messages__empty (translate :loading)]
    [:div.yle-livefeed__messages__container
     [loading-indicator loading?]
     [auto-scrolling-container topic-items best? focus-input]
     [unread-indicator]
     [feed-reactions/component]]))
